// =============================================================================
// features/policies/admin/PolicyAcceptancesPage.tsx — Tela de gestão: quem
// confirmou ciência da política interna, quando, e exportação em CSV para o
// RH anexar ao processo do empregado (F51-S17).
//
// Gate de UI: `policies:manage` (admin) — a permissão real é reaplicada no
// backend (doc: nunca confiar só no front); esconder aqui é cosmético.
//
// NOTA PARA O REVISOR: esta página ainda não está registrada em nenhuma rota
// do Manager (`apps/web/src/App.tsx` está fora de `files_allowed` deste
// slot — ver relatório do slot). O componente é completo e testado
// isoladamente; falta o `<Route path="/admin/policies/:key/acceptances" .../>`
// + item de navegação, que outro slot/commit pequeno precisa acrescentar.
// =============================================================================
import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import type { ResponsiveTableColumn } from '../../../components/ui/ResponsiveTable';
import { ResponsiveTable } from '../../../components/ui/ResponsiveTable';
import { useToast } from '../../../components/ui/Toast';
import { useAuth } from '../../../lib/auth-store';
import { exportPolicyAcceptances } from '../api';
import { usePolicyAcceptances } from '../queries';

const MANAGE_PERMISSION = 'policies:manage';

export interface PolicyAcceptancesPageProps {
  readonly policyKey: string;
  readonly policyTitle?: string;
}

interface AcceptanceRow {
  id: string;
  userId: string;
  userFullName: string | null;
  policyVersion: number;
  acceptedAt: string;
  ip: string | null;
  userAgent: string | null;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const COLUMNS: ResponsiveTableColumn<AcceptanceRow>[] = [
  {
    key: 'userFullName',
    header: 'Funcionário',
    primary: true,
    cell: (row) => row.userFullName ?? row.userId,
  },
  {
    key: 'policyVersion',
    header: 'Versão aceita',
    cell: (row) => `v${row.policyVersion}`,
  },
  {
    key: 'acceptedAt',
    header: 'Aceito em',
    cell: (row) => formatDateTime(row.acceptedAt),
  },
  {
    key: 'ip',
    header: 'IP',
    cell: (row) => row.ip ?? '—',
    hideBelow: 'md',
  },
  {
    key: 'userAgent',
    header: 'User-Agent',
    cell: (row) => row.userAgent ?? '—',
    hideBelow: 'lg',
  },
];

/**
 * PolicyAcceptancesPage — relação de quem confirmou ciência de uma política
 * interna, com exportação em CSV.
 */
export function PolicyAcceptancesPage({
  policyKey,
  policyTitle,
}: PolicyAcceptancesPageProps): React.JSX.Element {
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const canManage = hasPermission(MANAGE_PERMISSION);

  const acceptances = usePolicyAcceptances(policyKey, { limit: 100 }, { enabled: canManage });
  const [isExporting, setIsExporting] = React.useState(false);

  const handleExport = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const { blob, filename } = await exportPolicyAcceptances(policyKey);
      triggerDownload(blob, filename);
    } catch {
      toast('Não foi possível exportar a relação de aceites.', 'danger');
    } finally {
      setIsExporting(false);
    }
  }, [policyKey, toast]);

  if (!canManage) {
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <h1 className="font-display font-bold text-ink" style={{ fontSize: 'var(--text-2xl)' }}>
          Acesso restrito
        </h1>
        <p className="font-sans text-sm text-ink-3 mt-2">Esta tela é exclusiva da administração.</p>
      </div>
    );
  }

  const rows: AcceptanceRow[] = acceptances.data?.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1
            className="font-display font-bold text-ink leading-tight"
            style={{ fontSize: 'var(--text-2xl)', letterSpacing: '-0.03em' }}
          >
            Ciência de política — {policyTitle ?? policyKey}
          </h1>
          <p className="font-sans text-sm text-ink-3 mt-1">
            Quem confirmou ciência, qual versão e quando. Exporte em CSV para anexar ao processo do
            empregado.
          </p>
        </div>
        <Button variant="outline" onClick={() => void handleExport()} disabled={isExporting}>
          {isExporting ? 'Exportando…' : 'Exportar CSV'}
        </Button>
      </div>

      <ResponsiveTable
        columns={COLUMNS}
        data={rows}
        getRowKey={(row) => row.id}
        isLoading={acceptances.isLoading}
        aria-label="Relação de ciência da política"
        emptyState={
          <p className="font-sans text-sm text-ink-3 text-center py-12">
            Ninguém confirmou ciência ainda.
          </p>
        }
      />
    </div>
  );
}
